/**
 * Converts an "L C H" OKLCH channel string (as stored in our CSS variables,
 * e.g. "0.76 0.14 84") into a #rrggbb hex color.
 *
 * Canvas contexts do not resolve CSS variables, so chart colors read from the
 * theme tokens must be converted to a concrete color first. Falls back to null
 * when the value cannot be parsed.
 */
export function oklchChannelsToHex(channels: string): string | null {
  const parts = channels.trim().split(/[\s/]+/).filter(Boolean);
  if (parts.length < 3) return null;

  const lightness = Number(parts[0]);
  const chroma = Number(parts[1]);
  const hue = Number(parts[2]);
  if (!Number.isFinite(lightness) || !Number.isFinite(chroma) || !Number.isFinite(hue)) {
    return null;
  }

  const hueRad = (hue * Math.PI) / 180;
  const a = chroma * Math.cos(hueRad);
  const b = chroma * Math.sin(hueRad);

  const lPrime = lightness + 0.3963377774 * a + 0.2158037573 * b;
  const mPrime = lightness - 0.1055613458 * a - 0.0638541728 * b;
  const sPrime = lightness - 0.0894841775 * a - 1.291485548 * b;

  const l = lPrime ** 3;
  const m = mPrime ** 3;
  const s = sPrime ** 3;

  const r = 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s;
  const g = -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s;
  const bl = -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s;

  const toGamma = (value: number) => {
    const clamped = Math.min(1, Math.max(0, value));
    return clamped <= 0.0031308 ? 12.92 * clamped : 1.055 * clamped ** (1 / 2.4) - 0.055;
  };

  const channel = (value: number) =>
    Math.round(Math.min(1, Math.max(0, toGamma(value))) * 255)
      .toString(16)
      .padStart(2, '0');

  return `#${channel(r)}${channel(g)}${channel(bl)}`;
}